import React, { useState, useEffect } from 'react';
import { learningApi } from '../../utils/api';

const COLORS = {
  purple: '#667eea',
  purpleDark: '#764ba2',
  lightPurple: '#f0f7ff',
  gray: '#e0e0e0',
  text: '#2d3748',
  subtext: '#718096',
  gold: '#f6ad55',
  success: '#48bb78',
};

/**
 * 艾宾浩斯复习计划表组件
 * 以表格形式展示每个单词的复习周期完成情况
 */
export default function EbbinghausTable({ onGoHome }) {
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState(null);
  const [filterCompleted, setFilterCompleted] = useState(false);

  useEffect(() => {
    loadTableData();
  }, []);

  const loadTableData = async () => {
    try {
      setLoading(true);
      const data = await learningApi.getEbbinghausTable();
      setTableData(data);
    } catch (error) {
      console.error('加载艾宾浩斯复习计划表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 计算总复习进度
  const calculateProgress = () => {
    if (!tableData || tableData.words.length === 0) return 0;
    
    let totalCycles = 0;
    let completedCycles = 0;
    
    tableData.words.forEach(word => {
      const cycles = Object.values(word.review_cycles);
      totalCycles += cycles.length;
      completedCycles += cycles.filter(c => c.completed).length;
    });
    
    return totalCycles > 0 ? Math.round((completedCycles / totalCycles) * 100) : 0;
  };

  const renderCheckMark = (completed) => {
    if (completed) {
      return (
        <div style={{
          width: '32px',
          height: '32px',
          background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.purpleDark})`,
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '18px',
          fontWeight: 'bold',
        }}>
          ✓
        </div>
      );
    }
    return (
      <div style={{
        width: '32px',
        height: '32px',
        background: '#fff',
        border: '2px solid ' + COLORS.gray,
        borderRadius: '6px',
      }} />
    );
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          padding: '2rem',
          background: '#fff',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}>
          <div style={{ fontSize: '1.2rem', color: COLORS.text, textAlign: 'center' }}>
            加载中...
          </div>
        </div>
      </div>
    );
  }

  const words = tableData?.words || [];
  const progress = calculateProgress();

  return (
    <div style={{
      minHeight: '100vh',
      background: `linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)`,
      padding: '2rem 1rem',
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        background: '#fff',
        borderRadius: '16px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        overflow: 'hidden',
      }}>
        {/* 头部 */}
        <div style={{
          background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.purpleDark})`,
          padding: '2rem',
          color: '#fff',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '1.8rem' }}>
                📊 艾宾浩斯遗忘曲线计划表
              </h1>
              <p style={{ margin: 0, opacity: 0.9 }}>
                学习后自动生成复习计划，随时间推移更新分类
              </p>
            </div>
            <button
              onClick={onGoHome}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '2px solid rgba(255,255,255,0.3)',
                color: '#fff',
                padding: '0.8rem 1.5rem',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '600',
              }}
            >
              ⌂ 返回主页
            </button>
          </div>
        </div>

        {/* 统计信息 */}
        <div style={{ padding: '1.5rem 2rem', background: '#f8f9fa' }}>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ color: COLORS.subtext, fontSize: '0.9rem' }}>总单词数</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.purple }}>
                {tableData?.total_words || 0}
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ color: COLORS.subtext, fontSize: '0.9rem' }}>总复习进度</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.success }}>
                {progress}%
              </div>
            </div>
            <div style={{ flex: 1, minWidth: '200px' }}>
              <div style={{ color: COLORS.subtext, fontSize: '0.9rem' }}>已完成复习周期</div>
              <div style={{ fontSize: '2rem', fontWeight: 'bold', color: COLORS.gold }}>
                {words.reduce((sum, w) => 
                  sum + Object.values(w.review_cycles).filter(c => c.completed).length, 0
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 表格 */}
        <div style={{ padding: '2rem', overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            minWidth: '800px',
          }}>
            <thead>
              <tr style={{
                background: `linear-gradient(135deg, ${COLORS.purple}, ${COLORS.purpleDark})`,
                color: '#fff',
              }}>
                <th style={{ padding: '1rem', textAlign: 'left', borderRadius: '8px 0 0 0' }}>序号</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>单词</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>释义</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>学习时间</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>1天</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>2天</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>4天</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>7天</th>
                <th style={{ padding: '1rem', textAlign: 'center' }}>15天</th>
                <th style={{ padding: '1rem', textAlign: 'center', borderRadius: '0 8px 0 0' }}>30天</th>
              </tr>
            </thead>
            <tbody>
              {words.length === 0 ? (
                <tr>
                  <td colSpan="10" style={{
                    padding: '3rem',
                    textAlign: 'center',
                    color: COLORS.subtext,
                  }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}></div>
                    <div>还没有学习记录</div>
                    <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                      开始学习单词后，这里会显示您的复习计划
                    </div>
                  </td>
                </tr>
              ) : (
                words.map((word, index) => (
                  <tr
                    key={word.id}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      transition: 'background 0.2s',
                      cursor: 'pointer',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = COLORS.lightPurple}
                    onMouseLeave={(e) => e.currentTarget.style.background = '#fff'}
                  >
                    <td style={{ padding: '1rem', fontWeight: '600', color: COLORS.text }}>
                      {index + 1}
                    </td>
                    <td style={{ padding: '1rem', fontWeight: '600', color: COLORS.purple }}>
                      {word.word}
                    </td>
                    <td style={{ padding: '1rem', color: COLORS.subtext }}>
                      {word.translation}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: COLORS.subtext }}>
                      {word.learned_at}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {renderCheckMark(word.review_cycles["1_day"].completed)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {renderCheckMark(word.review_cycles["2_days"].completed)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {renderCheckMark(word.review_cycles["4_days"].completed)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {renderCheckMark(word.review_cycles["7_days"].completed)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {renderCheckMark(word.review_cycles["15_days"].completed)}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center' }}>
                      {renderCheckMark(word.review_cycles["30_days"].completed)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 图例说明 */}
        <div style={{
          padding: '1.5rem 2rem',
          background: '#f8f9fa',
          borderTop: '1px solid #e0e0e0',
        }}>
          <div style={{ display: 'flex', gap: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: '600', color: COLORS.text }}>图例说明:</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {renderCheckMark(true)}
              <span style={{ color: COLORS.subtext }}>已完成复习</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {renderCheckMark(false)}
              <span style={{ color: COLORS.subtext }}>待复习</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
